// src/pages/ResetPassword.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Stack,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    CircularProgress
} from "@mui/material";

import {toast, ToastContainer} from "react-toastify";
import logo from 'assets/images/sMLogoBlk.png';
import axiosInstance from "../../custom-axios";

const USER_TYPES = [
    { value: "user", label: "User" },
    { value: "advisor", label: "Advisor" },
    { value: "employer", label: "Employer" },
    { value: "company", label: "Company" }
];

export default function ResetPassword({ token: propToken = null, onResetSuccess = null , email = ''}) {

    const location = useLocation();
    const navigate = useNavigate();
    const timerRef = useRef(null);

    // 1) Resolve token sources (priority: prop -> nav state -> URL query -> sessionStorage)
    const urlParams = new URLSearchParams(location.search);
    const urlToken = urlParams.get("token") || null;

    const navToken = location.state?.resetToken ?? null;
    // const openVia = location.state?.open_via ?? null; // optional info from navigate(...)
    const loginType = location.state?.type ?? null;    // optional user-type from navigate(...)

    const storedToken = typeof window !== "undefined" ? sessionStorage.getItem("reset-token") : null;

    const resolvedToken = propToken ?? navToken ?? urlToken ?? storedToken ?? null;

    const TYPE_MAP = {
        1: "user",
        2: "advisor",
        3: "employer",
        4: "company"
    };


    // If we have a new navToken but nothing in sessionStorage -> store fallback
    useEffect(() => {
        if (navToken && !storedToken) {
            try {
                sessionStorage.setItem("reset-token", navToken || propToken);
            } catch (e) {
                console.warn("sessionStorage set failed", e);
            }
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navToken]);

    // UI state
    const [ui, setUi] = useState({
        loading: false,
        expired: false,
        resendLoading: false,
        resendDisabledUntil: null,
        resendEmail: email, //email as a props
        resendType: loginType ?? "user"
    });

    // RHF: single form instance used for both email-send and reset forms (fields differ)
    const { control, handleSubmit, watch, formState: { errors }, reset } = useForm({
        defaultValues: { old_password: "", new_password: "", confirm_password: "" }
    });

    const newPassword = watch("new_password");

    const patchUi = (patch) => setUi((s) => ({ ...s, ...patch }));

    // cooldown for resend
    const startCooldown = (seconds = 60) => {
        const until = Date.now() + seconds * 1000;
        patchUi({ resendDisabledUntil: until });
        timerRef.current = setInterval(() => {
            if (Date.now() >= until) {
                clearInterval(timerRef.current);
                patchUi({ resendDisabledUntil: null });
            }
        }, 500);
    };


    //   Submit reset password (when token present)

    const onSubmitReset = async (values) => {
        const token = resolvedToken;
        if (!token) {
            toast.error("Reset token missing. Please request a reset link or re-login.");
            return;
        }

        patchUi({ loading: true });

        try {
            const payload = {
                token,
                old_password: values.old_password,
                new_password: values.new_password
            };


            const res = await axiosInstance.post("api/reset-password", payload);

            const data = res;

            if (data.status === "success") {

                toast.success(data.message || "Password updated successfully.");

                try { sessionStorage.removeItem("reset-token"); } catch (e) {}
                // optional onResetSuccess callback
                if (typeof onResetSuccess === "function") {
                    onResetSuccess();
                } else {
                    navigate("/smartmoney/signin", { replace: true });
                }
                return;
            }

            if (data.status === "expired") {
                patchUi({ expired: true });
                toast.warning(data.message || "Reset token expired. Request a new link.");
                return;
            }

            // other failure
            toast.error(data.message || "Reset failed. Please verify your old password and try again.");
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
            console.error("Reset error:", err);
            toast.error(msg);
        } finally {
            patchUi({ loading: false });
        }
    };

    //   Send reset link (when no token present) — email input UI
    const handleSendLink = async () => {

        const email = ui.resendEmail?.trim();
        if (!email) {
            toast.error("Please enter your email.");
            return;
        }

        patchUi({ resendLoading: true });

        try {
            // If you want to require credentials (safer), change this endpoint to accept password too.
            // The backend should return neutral response.
            const res = await axiosInstance.post("api/resend-reset-token", {
                email,
                type: TYPE_MAP[ui.resendType] || "user"
            });


            if(res.status === 'ok'){
                toast.info(res?.message || "If the account exists, a reset link has been sent.");
                // sessionStorage.setItem("reset-token", res?.reset_token);
                sessionStorage.clear();
                localStorage.clear();
                startCooldown(60);
            } else {
                toast.error(res.message)
            }


        } catch (err) {
            console.error("Send link error:", err);
            toast.error(err?.response?.data?.message || "Unable to send reset link right now.");
        } finally {
            patchUi({ resendLoading: false });
        }
    };

    const backToSignIn = () => {
        try {
            sessionStorage.removeItem("reset-token");
            localStorage.clear();
        } catch (e) {}
        navigate("/smartmoney/signin");
    };

    // Render either Reset Form (token present) or Send-Link Form (no token)
    const showResetForm = Boolean(urlToken);

    return (
        <>
        <Box sx={{ minHeight: "100vh", background: "#E3F2FD", display: "flex", justifyContent: "center", alignItems: "center", p: 2 }}>
            <Card sx={{ width: 520, maxWidth: "98%" }}>
                <CardContent>
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                        <img src={logo} alt="Logo" style={{ width: 220 }} />
                    </Box>

                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {urlToken ? "Reset Your Password" : "Request Password Reset"}
                    </Typography>

                    {urlToken ? (
                        // ---------------- Reset form (old + new + confirm)
                        <Box component="form" onSubmit={handleSubmit(onSubmitReset)} noValidate>
                            <Stack spacing={2}>
                                <Controller
                                    name="old_password"
                                    control={control}
                                    rules={{ required: "Old password is required" }}
                                    render={({ field }) => (
                                        <TextField {...field} label="Old Password" type="password" fullWidth error={!!errors.old_password} helperText={errors.old_password?.message} disabled={ui.loading} />
                                    )}
                                />

                                <Controller
                                    name="new_password"
                                    control={control}
                                    rules={{
                                        required: "New password is required",
                                        minLength: {
                                            value: 8,
                                            message: "Password must be at least 8 characters long"
                                        },
                                        pattern: {
                                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
                                            message:
                                                "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
                                        }
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="New Password"
                                            type="password"
                                            fullWidth
                                            error={!!errors.new_password}
                                            helperText={errors.new_password?.message}
                                            disabled={ui.loading}
                                        />
                                    )}
                                />


                                <Controller
                                    name="confirm_password"
                                    control={control}
                                    rules={{ required: "Please confirm", validate: (v) => v === newPassword || "Passwords do not match" }}
                                    render={({ field }) => (
                                        <TextField {...field} label="Confirm Password" type="password" fullWidth error={!!errors.confirm_password} helperText={errors.confirm_password?.message} disabled={ui.loading} />
                                    )}
                                />

                                <Stack direction="row" spacing={2}>
                                    <Button variant="contained" type="submit" disabled={ui.loading}>
                                        {ui.loading ? <CircularProgress size={20} /> : "Update Password"}
                                    </Button>

                                    <Button variant="text" onClick={backToSignIn} disabled={ui.loading}>
                                        Back to Sign In
                                    </Button>
                                </Stack>

                                {/*<Typography variant="caption" color="text.secondary">*/}
                                {/*    Token present: Yes. It will be cleared after a successful reset.*/}
                                {/*</Typography>*/}
                            </Stack>
                        </Box>
                    ) : (
                        // ---------------- Send link form (email + optional type)
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Enter the email associated with your account and we will generate a password reset link.
                            </Alert>

                            <Stack spacing={2}>
                                <TextField
                                    label="Email"
                                    value={ui.resendEmail}
                                    onChange={(e) => patchUi({ resendEmail: e.target.value })}
                                    fullWidth
                                    disabled
                                    // disabled={ui.resendLoading && ui.resendEmail}
                                />

                                <Stack direction="row" spacing={2}>
                                    <Button variant="contained" onClick={handleSendLink} disabled={ui.resendLoading || !!ui.resendDisabledUntil}>
                                        {ui.resendLoading ? <CircularProgress size={20} /> : "Send Reset Link"}
                                    </Button>

                                    <Button variant="text" onClick={backToSignIn} disabled={ui.resendLoading}>
                                        Back to Sign In
                                    </Button>
                                </Stack>

                                {ui.resendDisabledUntil && <Typography variant="body2" color="text.secondary">Retry in {Math.ceil((ui.resendDisabledUntil - Date.now())/1000)}s</Typography>}
                            </Stack>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    {/*{loader}*/}
        <ToastContainer />
        </>
    );
}
